import PropTypes from 'prop-types';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Collapse,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
} from 'reactstrap';
import { Me, ReduxState } from 'types';
import {
  useDevelopmentPlanAdmin,
  useHasDirectReports,
  useIsAIAdmin,
  useIsHRAdmin,
  useIsObjectivesAdmin,
  useIsOneOnOneAdmin,
  useIsPromotionPacketAdmin,
  useIsSuperAdminNonImpersonating,
} from 'utils/models/User';
import {
  atLeastOneContinuousFeedbackFeatureIsEnabled,
  goalsAreEnabled,
  isSkillsAndBehaviorsDirectoryEnabled,
  objectivesAreEnabled,
  oneOnOnesAreEnabled,
  operatingManualsAreEnabled,
  performanceProfilesAreEnabled,
  resumesAreEnabled,
  surveysAndPerformanceNavIsEnabled,
  useFeatures,
} from 'utils/util/features';
import { useHoverMenu } from 'utils/util/hooks';
import * as consts from '../../consts/consts';
import { findAutoExpandedSection, getNavLinkIsActive } from './navActiveState';

const SiteNav: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const isHrAdmin = useIsHRAdmin();
  const isOneOnOneAdmin = useIsOneOnOneAdmin();
  const isObjectivesAdmin = useIsObjectivesAdmin();
  const isPromotionPacketAdmin = useIsPromotionPacketAdmin();
  const { isDevelopmentPlanAdmin } = useDevelopmentPlanAdmin();
  const isAIAdmin = useIsAIAdmin();
  const hasDirectReports = useHasDirectReports();
  const isSuperAdminNonImpersonating = useIsSuperAdminNonImpersonating();
  const { isEnabled, isGroupAndOptionEnabled } = useFeatures();

  const location = useLocation();

  const [manualCollapseId, setManualCollapseId] = useState<string | null>(null);
  const [hasManualToggle, setHasManualToggle] = useState(false);

  // when the left nav is collapsed to an icon rail, sections with children
  // open as a flyout menu next to their icon instead of expanding inline,
  // and icon-only links get their label as a tooltip
  const isCollapsed = !!props.isCollapsed;
  const rail = isCollapsed
    ? {
        sectionsAsFlyouts: true,
        titleFor: (name: string): string | undefined => name,
      }
    : {
        sectionsAsFlyouts: false,
        titleFor: (_name: string): string | undefined => undefined,
      };
  // closing is delayed slightly so the pointer can cross the gap between
  // the rail and the (portaled) menu without the menu disappearing
  const flyout = useHoverMenu();

  const toggleExpandedCollapseId = useCallback((id) => {
    setHasManualToggle(true);
    setManualCollapseId((prev) => (prev === id ? null : id));
  }, []);

  const collapseAllNavItems = useCallback(() => {
    // wait very briefly to avoid visual hang
    setTimeout(() => {
      setHasManualToggle(true);
      setManualCollapseId(null);
    }, 100);
  }, []);

  const showIfEnabled = props.showIfEnabled;
  const isSuperAdmin = props.isSuperAdmin;
  const isOrgSystemAdmin = props.isOrgSystemAdmin;

  // @ts-expect-error
  const features = useMemo(() => props.features || {}, [props.features]);
  const objectives_restrict_visibility_to_match_editability =
    // @ts-expect-error
    props?.settings?.objectives_restrict_visibility_to_match_editability ??
    false;
  const objectives_rollups_enabled =
    features?.objectives?.rollup_enabled ?? true;

  const surveysAndPerfEnabled = surveysAndPerformanceNavIsEnabled(features);
  const skillsAndBehaviorsDirectoryEnabled =
    isSkillsAndBehaviorsDirectoryEnabled(features);

  const aiAgentsVisibleToUser =
    isGroupAndOptionEnabled('ai_assistant', 'ai_agents') &&
    (props.visibleAgents?.length ?? 0) > 0;

  const hasAnyTeamItems = useMemo(() => {
    return (
      features.team_overview_dashboard?.enabled ||
      features.engagement?.enabled ||
      features.one_on_ones?.enabled ||
      features.continuous_feedback?.enabled ||
      surveysAndPerfEnabled ||
      features.development_plans?.enabled ||
      features.promotion_packets?.enabled ||
      features.objectives?.enabled ||
      features.custom_surveys?.enabled ||
      aiAgentsVisibleToUser
    );
  }, [features, surveysAndPerfEnabled, aiAgentsVisibleToUser]);

  const menuItems = useMemo(() => {
    const menuItems = [];

    // @ts-expect-error
    if (!props.errorMessage) {
      // @ts-expect-error
      menuItems.push(consts.HOME(formatMessage));

      const myProfileChildren = [
        ...(resumesAreEnabled(features)
          ? [consts.MY_OVERVIEW(formatMessage)]
          : []),
        ...(oneOnOnesAreEnabled(features)
          ? [consts.MY_ONE_ON_ONES(formatMessage)]
          : []),
        ...(objectivesAreEnabled(features)
          ? [consts.MY_OBJECTIVES(formatMessage)]
          : []),
        ...(goalsAreEnabled(features)
          ? [consts.MY_ASPIRATIONS(formatMessage)]
          : []),
        ...(operatingManualsAreEnabled(features)
          ? [consts.MY_MANUAL(formatMessage)]
          : []),
        ...(atLeastOneContinuousFeedbackFeatureIsEnabled(features)
          ? [consts.MY_FEEDBACK(formatMessage)]
          : []),
        ...(performanceProfilesAreEnabled(features)
          ? [consts.MY_PERFORMANCE(formatMessage)]
          : []),
        ...(features?.development_plans?.enabled
          ? [consts.MY_DEVELOPMENT_PLANS(formatMessage)]
          : []),
      ];

      if (myProfileChildren.length > 0) {
        // @ts-expect-error
        menuItems.push({
          name: formatMessage({
            id: 'app.consts.my_profile.nav_name',
            defaultMessage: 'Me',
          }),
          icon: consts.ICONS.PERSON,
          children: myProfileChildren,
        });
      }

      if (hasAnyTeamItems) {
        // @ts-expect-error
        menuItems.push({
          name: formatMessage({
            id: 'app.consts.team.name',
            defaultMessage: 'Team',
          }),
          icon: consts.ICONS.TEAM,
          children: [
            // TODO: efficiently determine if the current user is a people manager and if
            // not, do not show this link or show something else useful
            ...(aiAgentsVisibleToUser
              ? [consts.TEAM_AI_PROMPTS(formatMessage)]
              : []),
            ...showIfEnabled(
              'team_overview_dashboard',
              consts.TEAM(formatMessage)
            ),
            ...showIfEnabled(
              'engagement',
              consts.ENGAGEMENT_HUB(formatMessage)
            ),
            ...showIfEnabled(
              'one_on_ones',
              consts.TEAM_ONE_ON_ONES(formatMessage)
            ),
            ...showIfEnabled(
              'continuous_feedback',
              consts.TEAM_FEEDBACK_AND_RECOGNITION(formatMessage)
            ),
            ...(surveysAndPerfEnabled
              ? [consts.TEAM_PERFORMANCE_DASHBOARD_HEADER_LINK(formatMessage)]
              : []),
            ...showIfEnabled(
              'development_plans',
              consts.DEVELOPMENT_PLANS(formatMessage)
            ),
            ...showIfEnabled(
              'promotion_packets',
              consts.PROMOTION_PACKETS(formatMessage)
            ),
            ...showIfEnabled(
              'custom_surveys',
              consts.TEAM_LIFECYCLE_SURVEYS(formatMessage)
            ),
          ],
        });
      }

      const companyChildren = [
        ...showIfEnabled(
          'people',
          features.people?.org_chart
            ? consts.PEOPLE_ORG_CHART(formatMessage)
            : consts.PEOPLE_DIRECTORY(formatMessage)
        ),
        ...(skillsAndBehaviorsDirectoryEnabled
          ? [consts.SKILLS_DIRECTORY(formatMessage)]
          : []),
        ...showIfEnabled(
          'credentials',
          consts.CREDENTIALS_DIRECTORY(formatMessage)
        ),
        ...(objectives_rollups_enabled
          ? showIfEnabled('objectives', {
              ...consts.COMPANY_OBJECTIVES(formatMessage),
              exact: false,
            })
          : []),
        ...(!objectives_restrict_visibility_to_match_editability &&
        !objectives_rollups_enabled
          ? showIfEnabled('objectives', {
              ...consts.COMPANY_OBJECTIVES_TAB_PARTICIPATION(formatMessage),
              name: consts.COMPANY_OBJECTIVES(formatMessage).name,
              exact: false,
            })
          : []),
        ...showIfEnabled('levels', consts.LEVELING_FRAMEWORK(formatMessage)),
      ];

      if (companyChildren.length > 0) {
        // @ts-expect-error
        menuItems.push({
          name: formatMessage({
            id: 'app.consts.company.name',
            defaultMessage: 'Company',
          }),
          icon: consts.ICONS.COMPANY,
          children: companyChildren,
        });
      }

      // Permission to manage custom surveys: superuser, HRBP, or a manager
      // with active direct reports (CONF-434). System admins are
      // deliberately excluded here - custom surveys are HRBP-owned and
      // system admins get no special access to them (see
      // can_manage_custom_surveys in admin/api/logic/custom_surveys.py).
      const canManageCustomSurveys =
        isSuperAdmin || isHrAdmin || hasDirectReports;

      const isAIAdminOrSuperAdmin =
        isGroupAndOptionEnabled('ai_assistant', 'ai_agents') &&
        (isAIAdmin || isSuperAdminNonImpersonating);

      const isObjectivesAdminOrSuperAdmin =
        isGroupAndOptionEnabled('objectives', 'objective_types_enabled') &&
        (isObjectivesAdmin || isSuperAdminNonImpersonating);

      // Gated on the same superuser flag as the routes in routes.tsx, so the
      // menu never offers a section the route would deny
      const contentAdministrationEnabled = isEnabled('content_administration');
      const templatesAdministrationEnabled = isGroupAndOptionEnabled(
        'content_administration',
        'templates_enabled'
      );

      const showOneOnOnesAdministration =
        contentAdministrationEnabled &&
        isEnabled('one_on_ones') &&
        isEnabled('one_on_ones_administration') &&
        (isOneOnOneAdmin || isSuperAdmin);

      const showFeedbackAndRecognitionAdministration =
        contentAdministrationEnabled &&
        isEnabled('continuous_feedback') &&
        (isHrAdmin || isSuperAdmin);

      const showPromotionPacketsAdministration =
        templatesAdministrationEnabled &&
        isEnabled('promotion_packets') &&
        (isPromotionPacketAdmin || isSuperAdmin);

      const showDevelopmentPlansAdministration =
        templatesAdministrationEnabled &&
        isEnabled('development_plans') &&
        (isDevelopmentPlanAdmin || isSuperAdmin);

      if (
        isSuperAdmin ||
        isOrgSystemAdmin ||
        isAIAdminOrSuperAdmin ||
        isObjectivesAdminOrSuperAdmin ||
        showOneOnOnesAdministration ||
        showFeedbackAndRecognitionAdministration ||
        showPromotionPacketsAdministration ||
        showDevelopmentPlansAdministration ||
        (canManageCustomSurveys && isEnabled('custom_surveys')) ||
        isEnabled('nudges')
      ) {
        // @ts-expect-error
        menuItems.push({
          name: formatMessage({
            id: 'app.consts.admin.name',
            defaultMessage: 'Admin',
          }),
          icon: consts.ICONS.ADMIN,
          // Explicit order (not alphabetical) per CONF-434's Jira thread,
          // extended by CONF-516: People, Cycles, Lifecycle surveys, 1:1s,
          // Feedback & recognition, Promotion packets, Development plans,
          // Objectives, Nudges, AI agents & workflows, Integrations.
          // CONF-353 adds Organization and Features at the end for now.
          children: [
            ...(isSuperAdmin || isOrgSystemAdmin
              ? [consts.ADMINISTRATION_PEOPLE(formatMessage)]
              : []),
            ...((isSuperAdmin || isOrgSystemAdmin) && isEnabled('performance')
              ? [consts.ADMINISTRATION_CAMPAIGNS(formatMessage)]
              : []),
            ...(canManageCustomSurveys && isEnabled('custom_surveys')
              ? [consts.ADMINISTRATION_CUSTOM_SURVEYS(formatMessage)]
              : []),
            ...(showOneOnOnesAdministration
              ? [
                  {
                    ...consts.ADMINISTRATION_ONE_ON_ONES(formatMessage),
                    exact: false,
                  },
                ]
              : []),
            ...(showFeedbackAndRecognitionAdministration
              ? [
                  {
                    ...consts.ADMINISTRATION_FEEDBACK_AND_RECOGNITION(
                      formatMessage
                    ),
                    exact: false,
                  },
                ]
              : []),
            ...(showPromotionPacketsAdministration
              ? [
                  {
                    ...consts.ADMINISTRATION_PROMOTION_PACKETS(formatMessage),
                    exact: false,
                  },
                ]
              : []),
            ...(showDevelopmentPlansAdministration
              ? [
                  {
                    ...consts.ADMINISTRATION_DEVELOPMENT_PLANS(formatMessage),
                    exact: false,
                  },
                ]
              : []),
            ...(isObjectivesAdminOrSuperAdmin
              ? [
                  {
                    ...consts.ADMINISTRATION_OBJECTIVES(formatMessage),
                    exact: false,
                  },
                ]
              : []),
            ...showIfEnabled(
              'nudges',
              consts.NUDGES_ADMINISTRATION(formatMessage)
            ),
            ...(isAIAdminOrSuperAdmin ? [consts.AI(formatMessage)] : []),
            ...(isSuperAdmin || isOrgSystemAdmin
              ? [consts.APP_INTEGRATIONS(formatMessage)]
              : []),
            ...(isSuperAdmin || isOrgSystemAdmin
              ? [
                  consts.ADMINISTRATION_ORGANIZATION(formatMessage),
                  {
                    ...consts.ADMINISTRATION_FEATURES(formatMessage),
                    exact: false,
                  },
                ]
              : []),
          ],
        });
      }

      if (isEnabled('integration_learning_scaleup')) {
        // @ts-expect-error
        menuItems.push({
          ...consts.LEARNING(formatMessage),
          icon: consts.ICONS.LEARNING,
          dividerBefore: true,
        });
      }
    }

    // if any items have exactly one child, change the path to the child's path
    // and remove the child (so we don't have links with one item) for convenience
    return menuItems.map((item) => {
      // @ts-expect-error
      if (item?.children?.length === 1) {
        return {
          // @ts-expect-error
          ...item,
          // @ts-expect-error
          path: item.children[0].path,
          children: undefined,
        };
      }

      return item;
    });
  }, [
    // @ts-expect-error
    props.errorMessage,
    formatMessage,
    hasAnyTeamItems,
    showIfEnabled,
    skillsAndBehaviorsDirectoryEnabled,
    objectives_rollups_enabled,
    objectives_restrict_visibility_to_match_editability,
    isSuperAdmin,
    isOrgSystemAdmin,
    surveysAndPerfEnabled,
    isHrAdmin,
    isOneOnOneAdmin,
    hasDirectReports,
    isEnabled,
    isGroupAndOptionEnabled,
    isAIAdmin,
    isObjectivesAdmin,
    isPromotionPacketAdmin,
    isDevelopmentPlanAdmin,
    isSuperAdminNonImpersonating,
    aiAgentsVisibleToUser,
    features,
  ]);

  const meUrl = (props.me as Me)?.url;
  const autoExpandedCollapseId = useMemo(
    () => findAutoExpandedSection(menuItems, location.pathname, meUrl),
    [menuItems, location.pathname, meUrl]
  );

  // Reset manual override when location changes
  React.useEffect(() => {
    setHasManualToggle(false);
    setManualCollapseId(null);
    flyout.close();
  }, [location.pathname, flyout.close]);

  // no flyout should linger when switching back to the expanded nav
  React.useEffect(() => {
    if (!isCollapsed) {
      flyout.close();
    }
  }, [isCollapsed, flyout.close]);

  const expandedCollapseId = hasManualToggle
    ? manualCollapseId
    : autoExpandedCollapseId;

  return (
    <Nav navbar>
      {menuItems.flatMap((menuItem, menuItemIndex) => {
        const togglerId = 'site-nav-toggler-' + menuItemIndex;
        const isExpanded = expandedCollapseId === togglerId;
        // in the collapsed rail the section can't show its expanded child,
        // so highlight the section icon itself when one of its children is
        // the current page
        const hasActiveChild = autoExpandedCollapseId === togglerId;
        const title = rail.titleFor(menuItem.name);

        const renderedItem = (
          <NavItem key={menuItemIndex}>
            {!menuItem.external && !menuItem.children && (
              <NavLink
                exact={menuItem.exact ? menuItem.exact : true}
                to={menuItem.path}
                className="nav-link"
                onClick={collapseAllNavItems}
                title={title}
              >
                {menuItem.icon && <i className={menuItem.icon} />}
                <span className="left-navbar-label">{menuItem.name}</span>
              </NavLink>
            )}
            {!menuItem.external &&
              menuItem.children &&
              rail.sectionsAsFlyouts && (
                <Dropdown
                  direction="end"
                  isOpen={flyout.openId === togglerId}
                  toggle={() => flyout.toggle(togglerId)}
                  onMouseEnter={() => flyout.open(togglerId)}
                  onMouseLeave={flyout.closeSoon}
                  className="left-navbar-flyout"
                >
                  <DropdownToggle
                    tag="span"
                    role="button"
                    className={'nav-link' + (hasActiveChild ? ' active' : '')}
                    title={title}
                    aria-label={menuItem.name}
                  >
                    {menuItem.icon && <i className={menuItem.icon} />}
                  </DropdownToggle>
                  {/* rendered in <body>: the fixed left navbar is its own
                    stacking context (with z-index auto, see _custom.scss),
                    so a menu left inside it would paint underneath any
                    positioned page content such as cards */}
                  <DropdownMenu
                    container="body"
                    strategy="fixed"
                    className="left-navbar-flyout-menu"
                  >
                    <DropdownItem header>{menuItem.name}</DropdownItem>
                    {menuItem.children.map((item, index) => (
                      <DropdownItem
                        key={index}
                        tag={NavLink}
                        exact={item.exact !== undefined ? item.exact : true}
                        to={item.path}
                        isActive={getNavLinkIsActive(item, meUrl)}
                        onClick={flyout.close}
                      >
                        {item.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            {!menuItem.external &&
              menuItem.children &&
              !rail.sectionsAsFlyouts && (
                <>
                  <span
                    role="button"
                    className="nav-link"
                    data-bs-toggle="collapse"
                    aria-expanded={isExpanded ? 'true' : 'false'}
                    onClick={() => toggleExpandedCollapseId(togglerId)}
                  >
                    {menuItem.icon && <i className={menuItem.icon} />}
                    <span className="left-navbar-label">{menuItem.name}</span>
                  </span>
                  <Collapse isOpen={isExpanded}>
                    <ul className="nav nav-sm flex-column">
                      {menuItem.children.map((item, index) => (
                        <NavItem key={index}>
                          <NavLink
                            className="nav-link"
                            exact={item.exact !== undefined ? item.exact : true}
                            to={item.path}
                            isActive={getNavLinkIsActive(item, meUrl)}
                          >
                            {item.name}
                          </NavLink>
                        </NavItem>
                      ))}
                    </ul>
                  </Collapse>
                </>
              )}
            {menuItem.external && (
              <a href={menuItem.path} className="nav-link" title={title}>
                {menuItem.icon && <i className={menuItem.icon} />}
                <span className="left-navbar-label">{menuItem.name}</span>
              </a>
            )}
          </NavItem>
        );

        return menuItem.dividerBefore
          ? [
              <hr
                key={`divider-${menuItemIndex}`}
                className="navbar-divider my-3 mx-4"
              ></hr>,
              renderedItem,
            ]
          : renderedItem;
      })}
    </Nav>
  );
};

const SiteNav_propTypes = {
  me: PropTypes.object.isRequired,
  currentOrganization: PropTypes.object.isRequired,
  showIfEnabled: PropTypes.func.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  isOrgSystemAdmin: PropTypes.bool.isRequired,
  visibleAgents: PropTypes.array,
  // true when the left nav is collapsed to its icon-only rail (desktop)
  isCollapsed: PropTypes.bool,
};

type Props = PropTypes.InferProps<typeof SiteNav_propTypes>;

const mapStateToProps = (state: ReduxState) => {
  const {
    features,
    currentOrganization,
    currentProxyPerson,
    me,
    settings,
    visibleAgents,
  } = state;

  return {
    settings,
    features,
    currentOrganization,
    currentProxyPerson,
    me,
    visibleAgents,
  };
};

// all tracking in app will be passed through here
export default connect(mapStateToProps)(React.memo(SiteNav));
